import useServerTime from 'hooks/useServerTime';
import HomeLayout from 'layouts/HomeLayout';
import CenterValidation from 'pages/CenterValidation';
import VisibilityTester from 'pages/VisibilityTester';
import { lazy, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { getSessionItem } from 'utils/helper';
import { useButtonDialogBox } from 'hooks/useButtonDialogBox';
import useStore from 'store';
import useAccessToken from 'hooks/useAccessToken';

const StudentLogin = lazy(async () => import('pages/StudentLogin'));
const AdminLogin = lazy(async () => import('pages/AdminLogin'));
const SubjectSelection = lazy(async () => import('pages/SubjectSelection'));
const Instructions = lazy(async () => import('pages/Instructions'));
const Test = lazy(async () => import('pages/Test'));
const Journey = lazy(async () => import('pages/Journey'));
const StudentProfile = lazy(async () => import('pages/StudentProfile'));
const AdminProfile = lazy(async () => import('pages/AdminProfile'));

export const ROUTES = {
  STUDENT_LOGIN: '/student-login',
  ADMIN_LOGIN: '/admin-login',
  TASK: '/:index',
  ROOT: '/',
  HOME: '/home',
  INSTRUCTIONS: '/instructions',
  TEST: '/test',
  MISSION_SELECTION: '/mission-selection',
  STAGE_SELECTION: '/stage-selection',
  MODULE_PLAYER: '/module-player',
  JOURNEY: '/journey',
  STUDENT_PROFILE: '/student-profile',
  ADMIN_PROFILE: '/admin-profile',
};

function OpenRoutes() {
  const accessToken = useAccessToken();
  return accessToken ? <Navigate to={ROUTES.STUDENT_LOGIN} /> : <Outlet />;
}

function ProtectedRoutes() {
  const accessToken = useAccessToken();
  return accessToken ? <Outlet /> : <Navigate to={ROUTES.ROOT} />;
}

function StudentRoutes() {
  const { data, isFetched } = useServerTime();
  const { setServerTime, currentStudent } = useStore();
  const { showButtonDialogBox, closeDialog, open } = useButtonDialogBox();
  const [navigationConfirmed, setNavigationConfirmed] = useState(false);

  useEffect(() => {
    return () => showButtonDialogBox({ open: false });
  }, [showButtonDialogBox]);

  useEffect(() => {
    if (isFetched && data && data.serverTime) setServerTime(data.serverTime);
  }, [isFetched, data, setServerTime]);

  useEffect(() => {
    if (!currentStudent && !open) {
      showButtonDialogBox({
        open: true,
        title: 'Session Expired',
        subtitle: 'You have been logged out. Please login again.',
        rightButtonText: 'Login',
        onRightButtonClick: closeDialog,
        onClose: () => {
          setNavigationConfirmed(true);
        },
      });
    }
  }, [closeDialog, currentStudent, navigationConfirmed, open, showButtonDialogBox]);

  return navigationConfirmed ? <Navigate to={ROUTES.STUDENT_LOGIN} replace /> : <Outlet />;
}

function TestRoutes() {
  const { availabilityResponse } = useStore();
  return availabilityResponse ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function SubjectRoutes() {
  const { selectedSubject } = useStore();
  return selectedSubject ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function AdminRoutes() {
  const sessionId = getSessionItem('adminSessionId');
  return sessionId ? <Outlet /> : <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
}

function InvalidRouteHandler() {
  const accessToken = localStorage.getItem('accessToken');
  return <Navigate to={accessToken ? ROUTES.STUDENT_LOGIN : ROUTES.ROOT} />;
}

function AppRoutes() {
  /* 
  const navigate = useNavigate();
  const { logoutStudent } = useStore();
  const { onAdminLogout } = useAdminProfile();
  const state = useIdle(Constants.AUTO_LOGOUT_TIME);
  useEffect(() => {
    if (state) {
      logoutStudent();
      if (getSessionItem('adminSessionId')) onAdminLogout();
      navigate(ROUTES.STUDENT_LOGIN);
      window.location.reload();
    }
  }, [state, navigate, logoutStudent, onAdminLogout]);
   */

  return (
    <Routes>
      <Route element={<OpenRoutes />}>
        <Route path={ROUTES.ROOT} element={<CenterValidation />} />
      </Route>
      <Route element={<ProtectedRoutes />}>
        <Route path={ROUTES.STUDENT_LOGIN} element={<StudentLogin />} />
        <Route path={ROUTES.ADMIN_LOGIN} element={<AdminLogin />} />
        <Route element={<StudentRoutes />}>
          <Route path="/visibility-tester" element={<VisibilityTester />} />
          <Route path={ROUTES.HOME} element={<HomeLayout />}>
            <Route element={<SubjectSelection />} index />
          </Route>
          <Route element={<TestRoutes />}>
            <Route path={ROUTES.INSTRUCTIONS} element={<HomeLayout />}>
              <Route element={<Instructions />} index />
            </Route>
            <Route path={ROUTES.TEST} element={<Test />} />
          </Route>
          <Route element={<SubjectRoutes />}>
            <Route path={ROUTES.JOURNEY} element={<Journey />} />
          </Route>
          <Route path={ROUTES.STUDENT_PROFILE} element={<StudentProfile />} />
        </Route>
        <Route element={<AdminRoutes />}>
          <Route path={ROUTES.ADMIN_PROFILE} element={<AdminProfile />} />
        </Route>
      </Route>
      <Route path="*" element={<InvalidRouteHandler />} />
    </Routes>
  );
}
export default AppRoutes;
